import styled, { css } from 'styled-components'
import { Dialog } from '@base-ui/react/dialog'

import { tokens, zIndex } from '@styles/tokens'
import { EASE, duration } from '@styles/animations'

export const WindowPortal = styled(Dialog.Portal)`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
`

export const OverlayContainer = styled(Dialog.Backdrop)`
  position: absolute;
  inset: 0;
  background-color: var(--foreground---neutral, rgba(20, 23, 31, 0.6));
  opacity: 1;
  transition: opacity ${duration.overlay} ${EASE};
  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
  }
  &[data-ending-style] {
    transition-duration: ${duration.fast};
    pointer-events: none;
  }
`

const card = css`
  width: 90%;
  max-width: 480px;
  max-height: 75vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: ${tokens.radius.md};
  background: var(--persistent-white, #fff);
  box-shadow: ${tokens.shadow.xl};
`

export const PopUpContainer = styled.form`
  ${card}
`

export const DialogForm = styled(Dialog.Popup)`
  &[hidden] {
    display: none;
  }
  ${card}
  opacity: 1;
  transform: none;
  outline: none;
  transition: opacity ${duration.overlay} ${EASE}, transform ${duration.overlay} ${EASE};
  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: scale(0.96);
  }
  &[data-ending-style],
  &[data-closed] {
    pointer-events: none;
    transition-duration: ${duration.fast};
  }
`

export const Container = styled(Dialog.Viewport)`
  &[hidden] {
    display: none;
  }
  position: absolute;
  inset: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  &[data-ending-style],
  &[data-closed] {
    pointer-events: none;
  }
`

export const HeaderContainer = styled.header<{ hasChildren?: boolean; disableDivider?: boolean }>`
  width: 100%;
  min-height: calc(60px + ${({ hasChildren }) => (hasChildren ? 42 : 0)}px);
  border-bottom: ${({ disableDivider }) =>
    disableDivider ? 'none' : '1px solid var(--border-neutral-faded, #dfe2ea)'};
  padding: ${tokens.space.sm} ${tokens.space.xl};
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
`

export const HeaderTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const CloseButton = styled.button`
  border: none;
  background: transparent;
  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.focusRing};
  }
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  width: 36px;
  height: 36px;
  padding: 8px;
  box-sizing: border-box;

  cursor: pointer;

  border-radius: ${tokens.radius.sm};
  transition: background ${tokens.transition.fast};

  &:hover {
    background: rgba(20, 23, 31, 0.04);
  }

  svg {
    fill: var(--foreground-neutral, #14171f);
    transform: rotate(45deg);
  }
`

export const Title = styled.h1`
  padding: 0;
  box-sizing: border-box;

  color: ${tokens.palette.ink};
  font-size: 18px;
  font-style: normal;
  font-weight: ${tokens.fontWeight.medium};
  letter-spacing: -0.01em;
  line-height: 24px;
`

export const ScrollContainer = styled.div<{ overflow: 'auto' | 'hidden' }>`
  width: 100%;
  height: calc(100% - 60px);

  overflow-y: ${({ overflow }) => overflow};
`

export const MainContainer = styled.main<{ fullScreen?: boolean; disableBodyGutter?: boolean }>`
  min-height: 100%;

  margin: 0 auto;
  width: 100%;
  max-width: ${({ fullScreen }) => (fullScreen ? '100%' : '720px')};

  padding: ${({ disableBodyGutter }) => (disableBodyGutter ? '0' : `0 ${tokens.space.xl}`)};
  box-sizing: border-box;

  display: flex;
`

export const BottomContainer = styled.footer`
  width: 100%;
  height: 69px;
  border-top: 1px solid ${tokens.palette.borderFaded};

  padding: ${tokens.space.lg} ${tokens.space.xl};
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  /* Flat action button inside popups: no border, no shadow — but never clobber the keyboard focus ring */
  button:not([type='button']) {
    &:not(:focus-visible) {
      border-color: transparent;
      box-shadow: none;
    }

    &:hover:not(:disabled):not(:focus-visible) {
      box-shadow: none;
    }
  }
`

export const CancelButton = styled.button`
  border: none;
  outline: none;
  background-color: white;

  display: flex;
  justify-content: center;
  align-items: center;

  height: 36px;
  padding: 8px 12px;
  box-sizing: border-box;

  border-radius: ${tokens.radius.sm};

  color: ${tokens.palette.inkMuted};

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  cursor: pointer;

  transition: background ${tokens.transition.fast};

  &:hover {
    background: rgba(20, 23, 31, 0.04);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.focusRing};
    border-color: ${tokens.palette.primaryStrong};
  }
`

export const PreviousButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  padding: 8px;
  box-sizing: border-box;

  cursor: pointer;

  border: none;
  background: transparent;
  border-radius: ${tokens.radius.sm};
  transition: background ${tokens.transition.fast};

  &:hover {
    background: rgba(20, 23, 31, 0.04);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.focusRing};
  }

  svg {
    fill: var(--foreground-neutral, #14171f);
  }
`

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
`
